import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PLAN_LIMITS, PlanKey, WORDS_PER_PAGE, BASIC_HUMANIZER_PAGES_PER_MONTH } from '../config/planLimits';
import './PlanCards.css';

/* The plan cards from /landing (public/landing.html), shared by the homepage
   pricing section and the Subscription page so all three look the same.
   Limits come from config/planLimits.ts; feature lines match the ones the
   Subscription page has always shown. If you change a plan here, change
   public/landing.html too — it is static HTML and cannot import this file. */

export type BillingPeriod = 'monthly' | 'annual';

export interface PlanCardInfo {
  kicker: string;
  name: string;
  description: string;
  main: string[];
  more: string[];
}

const fmt = (n: number) => n.toLocaleString('en-US');
const books = (p: PlanKey) => {
  const n = PLAN_LIMITS[p].booksPerMonth;
  return n === 'unlimited' ? 'Unlimited books per month' : `${n} book${n === 1 ? '' : 's'} per month`;
};
const capacity = (p: PlanKey) => [
  books(p),
  `Up to ${fmt(PLAN_LIMITS[p].maxPagesPerBook)} pages per book (~${fmt(Math.round(PLAN_LIMITS[p].maxPagesPerBook * WORDS_PER_PAGE / 1000))}k words)`,
  `${fmt(PLAN_LIMITS[p].pagesPerMonth)} pages per month`,
  `${fmt(PLAN_LIMITS[p].pagesPerDay)} pages per day`,
];
const EXPORTS = 'Clean PDF, DOCX and EPUB exports';
const BASIC_HUMANIZER = `🛡️ AI Humanizer — ${BASIC_HUMANIZER_PAGES_PER_MONTH} pages/month`;
const PREMIUM_EXTRAS = ['Custom book templates', '🌍 Book Translator — translate books to 30+ languages'];

export const PLAN_CARDS: Record<string, PlanCardInfo> = {
  starter: { kicker: 'FREE PLAN', name: 'Starter', description: 'Explore the writing workflow for free.', main: capacity('starter'),
    more: ['No audiobooks', BASIC_HUMANIZER, 'Watermarked PDF export', 'AI-powered generation'] },
  writer: { kicker: 'REGULAR WRITING', name: 'Writer', description: 'Write regularly with clean exports and more room.', main: capacity('writer'),
    more: ['Audiobooks with Add-on', BASIC_HUMANIZER, EXPORTS, 'AI-powered generation', 'Priority support'] },
  author: { kicker: 'LONGER BOOKS', name: 'Author', description: 'Longer books and unlimited humanization.', main: capacity('author'),
    more: ['1 audiobook/month (+ Add-on)', '🛡️ AI Humanizer — Unlimited + Document Upload', EXPORTS, 'AI-powered generation', 'Priority support'] },
  pro: { kicker: 'MORE CAPACITY', name: 'Pro', description: 'More capacity, translation, and premium AI credits.', main: capacity('pro'),
    more: ['5 audiobooks/month (+ Add-on)', '🛡️ AI Humanizer — Unlimited + Document Upload + Priority', EXPORTS, 'AI-powered generation', 'Priority support', ...PREMIUM_EXTRAS, '⚡ 2,500 Premium AI credits/month'] },
  promax: { kicker: 'MAXIMUM CAPACITY', name: 'Pro Max', description: 'Maximum capacity and a founder onboarding call.', main: capacity('promax'),
    more: ['10 audiobooks/month (+ Add-on)', '🛡️ AI Humanizer — Unlimited + Document Upload + Priority', EXPORTS, 'AI-powered generation', 'Priority support', ...PREMIUM_EXTRAS, '⚡ 5,000 Premium AI credits/month', '30-minute Founder Onboarding Call included'] },
  humanizer: { kicker: 'HUMANIZER ONLY', name: 'AI Humanizer', description: 'Make your own writing read naturally. No book generation.',
    main: ['100 pages AI humanizer/month', 'Document upload (PDF, DOCX, TXT)', 'No book generation (humanizer only)'], more: [] },
};

/* Regular prices. Annual is the full yearly charge; `perMonth` is what the
   card shows under it. Same figures as Subscription's Stripe prices. */
export const PLAN_PRICES: Record<string, { monthly: number; annual: number; perMonth: string }> = {
  starter: { monthly: 0, annual: 0, perMonth: '0' },
  writer: { monthly: 9.99, annual: 95.90, perMonth: '7.99' },
  author: { monthly: 19.99, annual: 191.90, perMonth: '15.99' },
  pro: { monthly: 39.99, annual: 383.90, perMonth: '31.99' },
  promax: { monthly: 99.99, annual: 959.90, perMonth: '79.99' },
  humanizer: { monthly: 4.99, annual: 47.90, perMonth: '3.99' },
};

export const HOME_PLAN_ORDER = ['starter', 'writer', 'author', 'pro', 'promax'];

export function PlanPrice({ value, period, note, was, deal }: { value: string; period: string; note: string; was?: string; deal?: boolean }) {
  return (
    <p className="pc-price">
      {was && <s className="pc-price-was">{was}</s>}
      <b className={deal ? 'pc-price-deal' : undefined}>{value}</b>
      <span className="pc-price-period">{period}</span>
      <small>{note}</small>
    </p>
  );
}

/* Price for a plan at its regular rate. */
export function RegularPrice({ plan, billing }: { plan: string; billing: BillingPeriod }) {
  const p = PLAN_PRICES[plan];
  if (!p || p.monthly === 0) return <PlanPrice value="$0" period="USD / month" note="Free plan" />;
  return billing === 'annual'
    ? <PlanPrice value={`$${p.annual.toFixed(2)}`} period="USD / year" note={`$${p.perMonth}/mo billed annually`} />
    : <PlanPrice value={`$${p.monthly.toFixed(2)}`} period="USD / month" note="Billed monthly" />;
}

interface PlanCardProps {
  plan: string;
  /** Overrides for a plan this file does not know about (from the API). */
  info?: Partial<PlanCardInfo>;
  price: React.ReactNode;
  action: React.ReactNode;
  /** Shown between the price and the button, e.g. an add-on checkbox. */
  extras?: React.ReactNode;
  badge?: string;
  featured?: boolean;
  id?: string;
  label?: string;
}

export function PlanCard({ plan, info, price, action, extras, badge, featured, id, label }: PlanCardProps) {
  const card = { ...{ kicker: '', name: plan, description: '', main: [], more: [] }, ...PLAN_CARDS[plan], ...info } as PlanCardInfo;
  return (
    <article className={`pc-plan${featured ? ' pc-featured' : ''}`} data-plan={plan} id={id} aria-label={label}>
      <p className="pc-kicker">{card.kicker}{badge && <span className="pc-badge">{badge}</span>}</p>
      <h3 className="pc-title">{card.name}</h3>
      <p className="pc-description">{card.description}</p>
      {price}
      <div className="pc-extras">{extras}</div>
      <div className="pc-action">{action}</div>
      <ul className="pc-main" aria-label={`${card.name} plan capacity`}>
        {card.main.map(line => <li key={line}>{line}</li>)}
      </ul>
      {card.more.length > 0 ? (
        <details className="pc-details">
          <summary>
            <span className="pc-more">More details</span><span className="pc-less">Fewer details</span>
            <span className="pc-sr"> about {card.name}</span>
          </summary>
          <ul aria-label={`${card.name} additional features`}>
            {card.more.map(line => <li key={line}>{line}</li>)}
          </ul>
        </details>
      ) : <div className="pc-details pc-details-empty" />}
    </article>
  );
}

export function BillingToggle({ value, onChange }: { value: BillingPeriod; onChange: (v: BillingPeriod) => void }) {
  return (
    <div className="pc-billing">
      <div className="pc-billing-options" role="group" aria-label="Billing period">
        {(['monthly', 'annual'] as const).map(period => (
          <button key={period} type="button" aria-pressed={value === period} onClick={() => onChange(period)}>
            {period === 'monthly' ? 'Monthly' : 'Annual'}
          </button>
        ))}
      </div>
      <span className="pc-billing-saving">Save 20% annually</span>
    </div>
  );
}

/* Desktop: a grid. Phones: one card at a time with a glimpse of the next,
   plus arrows and dots — the same carousel as /landing. Native scroll snap
   does the work; the buttons only move it. */
export function PlanGrid({ children, label = 'Subscription plans', wide }: { children: React.ReactNode; label?: string; wide?: boolean }) {
  const grid = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const count = React.Children.toArray(children).filter(Boolean).length;
  const id = useRef(`pc-grid-${Math.random().toString(36).slice(2, 8)}`).current;

  const cards = () => Array.from(grid.current?.children || []) as HTMLElement[];
  const leftOf = (card: HTMLElement) => {
    const g = grid.current!;
    return card.getBoundingClientRect().left - g.getBoundingClientRect().left + g.scrollLeft;
  };
  const update = useCallback(() => {
    const g = grid.current;
    if (!g) return;
    const list = cards();
    let nearest = 0;
    list.forEach((card, i) => {
      if (Math.abs(leftOf(card) - g.scrollLeft) < Math.abs(leftOf(list[nearest]) - g.scrollLeft)) nearest = i;
    });
    setActive(nearest);
  }, []);
  const show = (index: number) => {
    const list = cards();
    const target = list[Math.max(0, Math.min(list.length - 1, index))];
    if (!target || !grid.current) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    grid.current.scrollTo?.({ left: leftOf(target), behavior: reduced ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);

  const names = React.Children.toArray(children).filter(Boolean).map((child: any) =>
    child?.props?.info?.name || PLAN_CARDS[child?.props?.plan]?.name || 'plan');
  return (
    <div className={`pc-wrap${wide ? ' pc-wide' : ''}`}>
      <div className="pc-grid" id={id} ref={grid} role="region" aria-label={label} tabIndex={0}
        style={{ '--pc-count': count } as React.CSSProperties}
        data-count={count}
        onScroll={update}
        onKeyDown={e => {
          if (e.target !== grid.current || !window.matchMedia?.('(max-width: 700px)').matches) return;
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); show(active + (e.key === 'ArrowRight' ? 1 : -1)); }
        }}>
        {children}
      </div>
      <div className="pc-carousel">
        <button type="button" aria-label="Previous plan" aria-controls={id} disabled={active === 0} onClick={() => show(active - 1)}>←</button>
        <div className="pc-dots">
          {Array.from({ length: count }, (_, i) => (
            <button key={i} type="button" aria-label={`Show ${names[i]} plan`} aria-controls={id}
              aria-current={i === active} onClick={() => show(i)} />
          ))}
        </div>
        <button type="button" aria-label="Next plan" aria-controls={id} disabled={active >= count - 1} onClick={() => show(active + 1)}>→</button>
      </div>
    </div>
  );
}
